import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import {
  updateAuthDestinationServicesBuyer,
  updateAuthDestinationServicesRenter
} from 'modules/authorizations/store/actions/destinationService.actions';
import { showToast } from 'modules/layout/layout.actions';
import React, { useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Divider
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { formatDateForInput } from 'utilities/common';
import { formatMoney } from 'utilities/currencyFormatter';
import Spinner from 'modules/common/spinner.component';
import AdvancedSearchComponent from 'modules/common/advancedSearch.component';
import { getAuthDestinationServices } from 'modules/authorizations/authorizations.actions';

const styles = theme => ({
  card: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  dialogIconRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '40px',
    paddingBottom: '20px'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  chipClickable: {
    cursor: 'pointer'
  },
  iconOverlayImg: {
    position: 'absolute',
    zIndex: '1 !important'
  },
  spacingX: {
    marginRight: theme.spacing(1)
  },
  footer: {
    height: 15,
    marginBottom: theme.spacing(2)
  },
  mb2: {
    marginBottom: '4px',
    marginRight: '16px'
  },
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important'
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important'
  },
  editModalInput: {
    minWidth: '332px'
  },
  dialogContentContainer: {
    margin: '15px',
    paddingLeft: '50px',
    paddingRight: '50px'
  },
  labelText: {
    minWidth: 350,
    marginBottom: 0
  },
  dialogPaper: {
    minHeight: '65vh',
    maxHeight: '65vh'
  }
});

export const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const EditCardModal = props => {
  const { open, onSubmit, isLoading, onClose, buyer, enumerations, type, renter, brokerAgents } = props;
  const classes = props.classes;

  // Enumerations
  const currencyOptions = enumerations?.currencies?.filter(s => s.isActive) ?? [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const typeOfHomeOptions = enumerations?.typeOfHomes?.filter(s => s.isActive) ?? [
    { id: '00000000-0000-0000-0000-000000000000', title: '' }
  ];

  const ageOfHomeOptions = enumerations?.ageOfHomes?.filter(s => s.isActive) ?? [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const propertyTypeOptions = enumerations?.propertyTypes?.filter(s => s.isActive) ?? [
    { id: '00000000-0000-0000-0000-000000000000', title: '' }
  ];

  const areaTourInfo = renter?.areaTourInfo ?? null;

  const familiarAreaOptions = ['Yes', 'No', 'Somewhat'];
  const basicPreferenceOptions = ['Yes', 'No', 'No Preference'];

  const [renterPriceRangeMin, setRenterPriceRangeMin] = useState(renter?.priceRangeMin ?? '');
  const [renterPriceRangeMax, setRenterPriceRangeMax] = useState(renter?.priceRangeMax ?? '');
  const [renterNumBedrooms, setRenterNumBedrooms] = useState(renter?.numBedrooms ?? 0);
  const [renterNumBathrooms, setRenterNumBathrooms] = useState(renter?.numBathrooms ?? 0);
  const [leaseStartDate, setLeaseStartDate] = useState(renter?.leaseStartDate ?? null);
  const [leaseEndDate] = useState(renter?.leaseEndDate ?? null);
  const [leaseAmount] = useState(renter?.leaseAmount ?? '');
  const [pentaltyDepositAmount, setPenaltyDepositAmount] = useState(renter?.penaltyDepositAmount ?? '');
  const [areaTour, setAreaTour] = useState((renter?.areaTourId ?? 0) !== 0);
  const [areaTourInitiationDate, setAreaTourInitiationDate] = useState(areaTourInfo?.areaTourInitiationDate ?? null);
  const [areaTourCanceledDate, setAreaTourCanceledDate] = useState(areaTourInfo?.areaTourCanceledDate ?? null);
  const [areaTourCompletedDate, setAreaTourCompletedDate] = useState(areaTourInfo?.areaTourCompletedDate ?? null);
  const [renterSchoolRequest, setRenterSchoolRequest] = useState(renter?.schoolRequest ?? false);
  const [renterSchoolRequestNote, setRenterSchoolRequestNote] = useState(renter?.schoolRequestNote ?? '');
  const [hasRentalSecured, setHasRentalSecured] = useState(renter?.hasRentalSecured ?? false);
  const [renterCurrency, setRenterCurrency] = useState(renter?.payInCurrencyId ?? ZERO_GUID);
  const [areaTourAgentId, setAreaTourAgentId] = useState(areaTourInfo?.areaTourAgentId);

  const [payInCurrencyId, setPayInCurrencyId] = useState(buyer?.payInCurrencyId ?? ZERO_GUID);
  const [priceRangeMin, setPriceRangeMin] = useState(buyer?.priceRangeMin ?? '');
  const [priceRangeMax, setPriceRangeMax] = useState(buyer?.priceRangeMax ?? '');
  const [numBedrooms, setNumBedrooms] = useState(buyer?.numBedrooms ?? 0);
  const [numBathrooms, setNumBathrooms] = useState(buyer?.numBathrooms ?? 0);
  const [typeOfHomeId, setTypeOfHomeId] = useState(buyer?.typeOfHomeId || '');
  const [ageOfHomeId, setAgeOfHomeId] = useState(buyer?.ageOfHomeId || '');
  const [squareFeet, setSquareFeet] = useState(buyer?.squareFeet || '');
  const [propertyTypeId, setPropertyTypeId] = useState(buyer?.propertyTypeId || '');
  const [familiarWithArea, setFamiliarWithArea] = useState(buyer?.familiarWithArea || '');
  const [fireplace, setFireplace] = useState(buyer?.fireplace || '');
  const [garage, setGarage] = useState(buyer?.garage || '');
  const [basement, setBasement] = useState(buyer?.basement || '');
  const [hasSchoolRequest, setHasSchoolRequest] = useState(buyer?.hasSchoolRequest ?? false);
  const [schoolRequestNote, setSchoolRequestNote] = useState(buyer?.schoolRequestNote || '');
  const [hasPreferredAgent, setHasPreferredAgent] = useState(buyer?.hasPreferredAgent ?? false);
  const [preferredAgentName, setPreferredAgentName] = useState(buyer?.preferredAgentName || '');
  const [preferredAgency, setPreferredAgency] = useState(buyer?.preferredAgency || '');
  const [preferredAgencyEmail, setPreferredAgencyEmail] = useState(buyer?.preferredAgencyEmail || '');
  const [preferredAgencyPhone, setPreferredAgencyPhone] = useState(buyer?.preferredAgencyPhone || '');
  //const [mortgageLenderSelected, setMortgageLenderSelected] = useState(buyer?.mortgageLenderSelected || '');
  const [specialNeeds, setSpecialNeeds] = useState(buyer?.specialNeeds ?? '');

  const onSubmitModal = e => {
    e.preventDefault();

    const values = {
      renterPriceRangeMin,
      renterPriceRangeMax,
      renterNumBedrooms,
      renterNumBathrooms,
      leaseStartDate,
      leaseEndDate,
      leaseAmount,
      pentaltyDepositAmount,
      areaTour,
      areaTourInitiationDate,
      areaTourCanceledDate,
      areaTourCompletedDate,

      renterSchoolRequest,
      renterSchoolRequestNote,
      hasRentalSecured,
      renterCurrency,
      areaTourAgentId,

      payInCurrencyId,
      priceRangeMin,
      priceRangeMax,
      numBedrooms,
      numBathrooms,
      typeOfHomeId,
      ageOfHomeId,
      squareFeet,
      propertyTypeId,
      familiarWithArea,
      fireplace,
      garage,
      basement,
      hasSchoolRequest,
      schoolRequestNote,
      hasPreferredAgent,
      preferredAgentName,
      preferredAgency,
      preferredAgencyEmail,
      preferredAgencyPhone,
     // mortgageLenderSelected,
      specialNeeds
    };

    onSubmit(values);
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'}>
      <form onSubmit={onSubmitModal} fullScreen="true">
        <div className="row justify-space-between align-center">
          <div className={classes.dialogIconRow}>
            <Tooltip title="Estimates" key="Estimates" placement="right">
              <FontAwesomeIcon icon={['fas', 'fa-truck-moving']} size="3x" />
            </Tooltip>
            <Typography variant="h5" sx={{ paddingLeft: '20px' }}>
              {type === 'Renter' ? 'Renter Preferences' : 'Buyer Preferences'}
            </Typography>
          </div>
        </div>

        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem'
            }}
          >
            <Grid container spacing={{ xs: 2, md: 3, lg: 5 }} sx={{ padding: '30px', paddingTop: '0px' }}>
              {type === 'Renter' && (
                <>
                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                        <FormControlLabel
                          key={'areaTour'}
                          name={'areaTour'}
                          label={'Area Tour'}
                          labelPlacement="start"
                          control={<Checkbox />}
                          required={false}
                          disabled={false}
                          checked={areaTour}
                          variant="standard"
                          margin="dense"
                          sx={{ marginTop: '6px', minWidth: '120px', maxWidth: '360px' }}
                          onClick={e => setAreaTour(e.target.checked ? true : false)}
                        />
                      </FormControl>
                    </div>
                  </Grid>
                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Currency</InputLabel>
                        <Select
                          labelId={'Currency'}
                          key={'renterCurrency'}
                          label={'Currency'}
                          name={'renterCurrency'}
                          required={false}
                          disabled={false}
                          value={renterCurrency}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setRenterCurrency(e.target.value)}
                        >
                          {currencyOptions.map(opt => (
                            <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>
                  <Grid item>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2">Min Price</Typography>
                      </div>
                      <TextField
                        key={'renterPriceRangeMin'}
                        label={''}
                        name={'renterPriceRangeMin'}
                        required={false}
                        disabled={false}
                        value={renterPriceRangeMin}
                        sx={{ maxWidth: '360px' }}
                        type="text"
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setRenterPriceRangeMin(e.target.value)}
                      />
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2">Max Price</Typography>
                      </div>
                      <TextField
                        key={'renterPriceRangeMax'}
                        label={''}
                        name={'renterPriceRangeMax'}
                        required={false}
                        disabled={false}
                        value={renterPriceRangeMax}
                        sx={{ maxWidth: '360px' }}
                        type="text"
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setRenterPriceRangeMax(e.target.value)}
                      />
                    </Box>
                  </Grid>
                  <Grid item>
                    <div>
                      <TextField
                        key={'renterNumBedrooms'}
                        label={'Bedroom Preference'}
                        name={'renterNumBedrooms'}
                        required={false}
                        value={renterNumBedrooms}
                        margin="dense"
                        disabled={false}
                        sx={{ maxWidth: '360px' }}
                        variant="standard"
                        type="number"
                        onChange={e => setRenterNumBedrooms(e.target.value)}
                      />
                    </div>
                  </Grid>
                  <Grid item>
                    <div>
                      <TextField
                        key={'renterNumBathrooms'}
                        label={'Bathroom Preference'}
                        name={'renterNumBathrooms'}
                        required={false}
                        value={renterNumBathrooms}
                        margin="dense"
                        disabled={false}
                        sx={{ maxWidth: '360px' }}
                        variant="standard"
                        type="number"
                        onChange={e => setRenterNumBathrooms(e.target.value)}
                      />
                    </div>
                  </Grid>
                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                        <FormControlLabel
                          key={'renterSchoolRequest'}
                          name={'renterSchoolRequest'}
                          label={'School Request'}
                          labelPlacement="start"
                          control={<Checkbox />}
                          required={false}
                          disabled={false}
                          checked={renterSchoolRequest}
                          variant="standard"
                          margin="dense"
                          sx={{ marginTop: '6px', minWidth: '120px', maxWidth: '360px' }}
                          onClick={e => setRenterSchoolRequest(e.target.checked ? true : false)}
                        />
                      </FormControl>
                    </div>
                  </Grid>
                  {renterSchoolRequest && (
                    <Grid item>
                      <div>
                        <TextField
                          key={'renterSchoolRequestNote'}
                          label={'School Request Note'}
                          name={'renterSchoolRequestNote'}
                          required={false}
                          disabled={false}
                          value={renterSchoolRequestNote}
                          sx={{ maxWidth: '360px' }}
                          margin="dense"
                          variant="standard"
                          type="text"
                          onChange={e => setRenterSchoolRequestNote(e.target.value)}
                        />
                      </div>
                    </Grid>
                  )}
                  {areaTour && (
                    <>
                      <Box sx={{ width: '100%' }}>
                        <hr />
                      </Box>
                      <Typography variant="h5" sx={{ paddingLeft: '20px', marginTop: '10px', marginBottom: '10px' }}>
                        Area Tour
                      </Typography>
                      <Grid item>
                        <div>
                          <AdvancedSearchComponent
                            labelText={'Area Tour Agent'}
                            displayValueAccessorKey={'firstName'}
                            saveIdValueAccessorKey={'agentId'}
                            searchingCriteria={[
                              { name: 'agentId', title: 'Id' },
                              { name: 'agentTypeIdReadable', title: 'Type of Agent' },
                              { name: 'firstName', title: 'First Name' },
                              { name: 'lastName', title: 'Last Name' },
                              { name: 'companyName', title: 'Company Name' },
                              { name: 'citiesServed', title: 'City' }
                            ]}
                            searchingCriteriaExtensions={[
                              { name: 'agentId', width: 100 },
                              { columnName: 'agentTypeIdReadable', width: 150 },
                              { columnName: 'firstName', width: 200 },
                              { columnName: 'lastName', width: 200 },
                              { columnName: 'companyName', width: 300 },
                              { columnName: 'citiesServed', width: 150 }
                            ]}
                            searchingRows={brokerAgents.map(agent => ({
                              ...agent,
                              agentTypeIdReadable: enumerations.agentTypes.find(type => type.id === agent.agentTypeId)?.title ?? ''
                            }))}
                            id={'agentId'}
                            value={areaTourAgentId}
                            setIdValue={(id, value) => {
                              setAreaTourAgentId(id);
                            }}
                            classes={classes}
                          />
                        </div>
                      </Grid>
                      <Grid item>
                        <div>
                          <TextField
                            key={'areaTourInitiationDate'}
                            label={'Area Tour Initiation Date'}
                            name={'areaTourInitiationDate'}
                            required={false}
                            disabled={false}
                            value={formatDateForInput(areaTourInitiationDate)}
                            sx={{ maxWidth: '360px' }}
                            margin="dense"
                            variant="standard"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            onChange={e => setAreaTourInitiationDate(e.target.value)}
                          />
                        </div>
                      </Grid>
                      <Grid item>
                        <div>
                          <TextField
                            key={'areaTourCanceledDate'}
                            label={'Area Tour Canceled Date'}
                            name={'areaTourCanceledDate'}
                            required={false}
                            disabled={false}
                            value={formatDateForInput(areaTourCanceledDate)}
                            sx={{ maxWidth: '360px' }}
                            margin="dense"
                            variant="standard"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            onChange={e => setAreaTourCanceledDate(e.target.value)}
                          />
                        </div>
                      </Grid>
                      <Grid item>
                        <div>
                          <TextField
                            key={'areaTourCompletedDate'}
                            label={'Area Tour Completed Date'}
                            name={'areaTourCompletedDate'}
                            required={false}
                            disabled={false}
                            value={formatDateForInput(areaTourCompletedDate)}
                            sx={{ maxWidth: '360px' }}
                            margin="dense"
                            variant="standard"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            onChange={e => setAreaTourCompletedDate(e.target.value)}
                          />
                        </div>
                      </Grid>
                    </>
                  )}
                  <Box sx={{ width: '100%', marginTop: '20px' }}>
                    <hr />
                  </Box>
                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                        <FormControlLabel
                          key={'hasRentalSecured'}
                          name={'hasRentalSecured'}
                          label={'Rental Secured'}
                          labelPlacement="start"
                          control={<Checkbox />}
                          required={false}
                          disabled={false}
                          checked={hasRentalSecured}
                          variant="standard"
                          margin="dense"
                          sx={{ marginTop: '6px', minWidth: '120px', maxWidth: '360px' }}
                          onClick={e => setHasRentalSecured(e.target.checked ? true : false)}
                        />
                      </FormControl>
                    </div>
                  </Grid>
                  {hasRentalSecured && (
                    <>
                      <Grid item>
                        <div>
                          <TextField
                            key={'leaseStartDate'}
                            label={'Lease Start Date'}
                            name={'leaseStartDate'}
                            required={false}
                            disabled={false}
                            value={formatDateForInput(leaseStartDate)}
                            sx={{ maxWidth: '360px' }}
                            margin="dense"
                            variant="standard"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            onChange={e => setLeaseStartDate(e.target.value)}
                          />
                        </div>
                      </Grid>
                      <Grid item>
                        <div>
                          <TextField
                            key={'pentaltyDepositAmount'}
                            label={'Deposit'}
                            name={'pentaltyDepositAmount'}
                            required={false}
                            value={pentaltyDepositAmount}
                            disabled={false}
                            margin="dense"
                            variant="standard"
                            sx={{ maxWidth: '360px' }}
                            onChange={e => setPenaltyDepositAmount(e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            allowDecimal={true}
                          />
                        </div>
                      </Grid>
                    </>
                  )}
                </>
              )}
              {type === 'Buyer' && (
                <>
                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Currency</InputLabel>
                        <Select
                          labelId={'buyerCurrency'}
                          key={'buyerCurrency'}
                          label={'Currency'}
                          name={'buyerCurrency'}
                          required={false}
                          disabled={false}
                          value={payInCurrencyId}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setPayInCurrencyId(e.target.value)}
                        >
                          {currencyOptions.map(opt => (
                            <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2">Min Price</Typography>
                      </div>
                      <TextField
                        key={'priceRangeMin'}
                        label={''}
                        name={'priceRangeMin'}
                        required={false}
                        disabled={false}
                        value={priceRangeMin}
                        sx={{ maxWidth: '360px' }}
                        type="text"
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setPriceRangeMin(e.target.value)}
                      />
                    </Box>
                  </Grid>

                  <Grid item>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2">Max Price</Typography>
                      </div>
                      <TextField
                        key={'priceRangeMax'}
                        label={''}
                        name={'priceRangeMax'}
                        required={false}
                        disabled={false}
                        value={priceRangeMax}
                        sx={{ maxWidth: '360px' }}
                        type="text"
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setPriceRangeMax(e.target.value)}
                      />
                    </Box>
                  </Grid>

                  <Grid item>
                    <div>
                      <TextField
                        key={'numBedrooms'}
                        label={'Bedroom Preference'}
                        name={'numBedrooms'}
                        required={false}
                        value={numBedrooms}
                        margin="dense"
                        disabled={false}
                        sx={{ maxWidth: '360px' }}
                        variant="standard"
                        type="number"
                        onChange={e => setNumBedrooms(e.target.value)}
                      />
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <TextField
                        key={'numBathrooms'}
                        label={'Bathroom Preference'}
                        name={'numBathrooms'}
                        required={false}
                        value={numBathrooms}
                        margin="dense"
                        disabled={false}
                        sx={{ maxWidth: '360px' }}
                        variant="standard"
                        type="number"
                        onChange={e => setNumBathrooms(e.target.value)}
                      />
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Type of Home</InputLabel>
                        <Select
                          labelId={'typeOfHomeId'}
                          key={'typeOfHomeId'}
                          label={'Type of Home'}
                          name={'typeOfHomeId'}
                          required={false}
                          disabled={false}
                          value={typeOfHomeId}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setTypeOfHomeId(e.target.value)}
                        >
                          {typeOfHomeOptions.map(opt => (
                            <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Age of Home</InputLabel>
                        <Select
                          labelId={'ageOfHomeId'}
                          key={'ageOfHomeId'}
                          label={'Age of Home'}
                          name={'ageOfHomeId'}
                          required={false}
                          disabled={false}
                          value={ageOfHomeId}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setAgeOfHomeId(e.target.value)}
                        >
                          {ageOfHomeOptions.map(opt => (
                            <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <TextField
                        key={'squareFeet'}
                        label={'Square Feet'}
                        name={'squareFeet'}
                        required={false}
                        value={squareFeet}
                        margin="dense"
                        disabled={false}
                        sx={{ maxWidth: '360px' }}
                        variant="standard"
                        type="text"
                        onChange={e => setSquareFeet(e.target.value)}
                      />
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Property Type</InputLabel>
                        <Select
                          labelId={'propertyTypeId'}
                          key={'propertyTypeId'}
                          label={'Property Type'}
                          name={'propertyTypeId'}
                          required={false}
                          disabled={false}
                          value={propertyTypeId}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setPropertyTypeId(e.target.value)}
                        >
                          {propertyTypeOptions.map(opt => (
                            <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                              {opt.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Familiar with Area</InputLabel>
                        <Select
                          labelId={'familiarWithArea'}
                          key={'familiarWithArea'}
                          label={'Familiar with Area'}
                          name={'familiarWithArea'}
                          required={false}
                          disabled={false}
                          value={familiarWithArea}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setFamiliarWithArea(e.target.value)}
                        >
                          {familiarAreaOptions.map(opt => (
                            <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Fireplace Preference</InputLabel>
                        <Select
                          labelId={'fireplace'}
                          key={'fireplace'}
                          label={'Fireplace Preference'}
                          name={'fireplace'}
                          required={false}
                          disabled={false}
                          value={fireplace}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setFireplace(e.target.value)}
                        >
                          {basicPreferenceOptions.map(opt => (
                            <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Garage Preference</InputLabel>
                        <Select
                          labelId={'garage'}
                          key={'garage'}
                          label={'Garage Preference'}
                          name={'garage'}
                          required={false}
                          disabled={false}
                          value={garage}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setGarage(e.target.value)}
                        >
                          {basicPreferenceOptions.map(opt => (
                            <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Basement Preference</InputLabel>
                        <Select
                          labelId={'basement'}
                          key={'basement'}
                          label={'Basement Preference'}
                          name={'basement'}
                          required={false}
                          disabled={false}
                          value={basement}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setBasement(e.target.value)}
                        >
                          {basicPreferenceOptions.map(opt => (
                            <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                        <FormControlLabel
                          key={'hasSchoolRequest'}
                          name={'hasSchoolRequest'}
                          label={'School Request'}
                          labelPlacement="start"
                          control={<Checkbox />}
                          required={false}
                          disabled={false}
                          checked={hasSchoolRequest}
                          variant="standard"
                          margin="dense"
                          sx={{ marginTop: '6px', minWidth: '120px', maxWidth: '360px' }}
                          onClick={e => setHasSchoolRequest(e.target.checked ? true : false)}
                        />
                      </FormControl>
                    </div>
                  </Grid>

                  {hasSchoolRequest && (
                    <Grid item>
                      <div>
                        <TextField
                          key={'schoolRequestNote'}
                          label={'School Request Note'}
                          name={'schoolRequestNote'}
                          required={false}
                          disabled={false}
                          value={schoolRequestNote}
                          sx={{ maxWidth: '360px' }}
                          margin="dense"
                          variant="standard"
                          type="text"
                          onChange={e => setSchoolRequestNote(e.target.value)}
                        />
                      </div>
                    </Grid>
                  )}

                  <Grid item>
                    <div>
                      <TextField
                        key={'specialNeeds'}
                        label={'Special Needs and Important Features'}
                        name={'specialNeeds'}
                        required={false}
                        disabled={false}
                        value={specialNeeds}
                        type="text"
                        margin="dense"
                        multiline
                        variant="standard"
                        rows={2}
                        sx={{ width: '550px' }}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setSpecialNeeds(e.target.value)}
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ margin: '20px 0' }} />
                  </Grid>

                  <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                        <FormControlLabel
                          key={'hasPreferredAgent'}
                          name={'hasPreferredAgent'}
                          label={'Has Preferred Agent'}
                          labelPlacement="start"
                          control={<Checkbox />}
                          required={false}
                          disabled={false}
                          checked={hasPreferredAgent}
                          variant="standard"
                          margin="dense"
                          sx={{ marginTop: '6px', minWidth: '120px', maxWidth: '360px' }}
                          onClick={e => setHasPreferredAgent(e.target.checked ? true : false)}
                        />
                      </FormControl>
                    </div>
                  </Grid>

                  {hasPreferredAgent && (
                    <>
                      <Grid item>
                        <div>
                          <TextField
                            key={'preferredAgentName'}
                            label={'Preferred Agent Name'}
                            name={'preferredAgentName'}
                            required={false}
                            value={preferredAgentName}
                            margin="dense"
                            disabled={false}
                            sx={{ maxWidth: '360px' }}
                            variant="standard"
                            type="text"
                            onChange={e => setPreferredAgentName(e.target.value)}
                          />
                        </div>
                      </Grid>

                      <Grid item>
                        <div>
                          <TextField
                            key={'preferredAgency'}
                            label={'Preferred Agency'}
                            name={'preferredAgency'}
                            required={false}
                            value={preferredAgency}
                            margin="dense"
                            disabled={false}
                            sx={{ maxWidth: '360px' }}
                            variant="standard"
                            type="text"
                            onChange={e => setPreferredAgency(e.target.value)}
                          />
                        </div>
                      </Grid>

                      <Grid item>
                        <div>
                          <TextField
                            key={'preferredAgencyEmail'}
                            label={'Agency Email'}
                            name={'preferredAgencyEmail'}
                            required={false}
                            value={preferredAgencyEmail}
                            margin="dense"
                            disabled={false}
                            sx={{ maxWidth: '360px' }}
                            variant="standard"
                            type="text"
                            onChange={e => setPreferredAgencyEmail(e.target.value)}
                          />
                        </div>
                      </Grid>

                      <Grid item>
                        <div>
                          <TextField
                            key={'preferredAgencyPhone'}
                            label={'Agency Phone'}
                            name={'preferredAgencyPhone'}
                            required={false}
                            value={preferredAgencyPhone}
                            margin="dense"
                            disabled={false}
                            sx={{ maxWidth: '360px' }}
                            variant="standard"
                            type="text"
                            onChange={e => setPreferredAgencyPhone(e.target.value)}
                          />
                        </div>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ margin: '20px 0' }} />
                  </Grid>

                  {/* <Grid item>
                    <div>
                      <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Mortgage Lender Selected</InputLabel>
                        <Select
                          labelId={'mortgageLenderSelected'}
                          key={'mortgageLenderSelected'}
                          label={'Mortgage Lender Selected'}
                          name={'mortgageLenderSelected'}
                          required={false}
                          disabled={false}
                          value={mortgageLenderSelected}
                          margin="dense"
                          variant="standard"
                          sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                          onChange={e => setMortgageLenderSelected(e.target.value)}
                        >
                          {['Premia', 'PNC', 'Rocket', 'Other'].map(opt => (
                            <MenuItem key={opt} value={opt} sx={{ margin: 'dense' }}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid> */}
                </>
              )}
            </Grid>
          </Stack>
        </div>

        {isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        )}
        {!isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button color="primary" type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

const DestinationServicesMainEditableCard = props => {
  const [isLoading, setIsLoading] = useState(false);
  const [editingModalOpen, setEditingModalOpen] = useState(false);

  const classes = props.classes;

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const { authId, buyer, renter, type, enumerations, brokerAgents } = props;

  const areaTour = renter?.areaTourInfo && renter?.areaTourId ? renter.areaTourInfo : null;

  const isFailure = r => r?.type?.endsWith('_FAILURE');

  // Helpers to map Guid -> title for display
  const mapEnumTitle = (list, id) => list?.find(x => x.id === id)?.title || '';

  const displayCurrencyTitle = currency => mapEnumTitle(enumerations?.currencies || [], currency);

  const displayTypeOfHome = () => mapEnumTitle(enumerations?.typeOfHomes || [], buyer?.typeOfHomeId);

  const displayAgeOfHome = () => mapEnumTitle(enumerations?.ageOfHomes || [], buyer?.ageOfHomeId);

  const displayPropertyType = () => mapEnumTitle(enumerations?.propertyTypes || [], buyer?.propertyTypeId);

  const updateFields = async values => {
    setIsLoading(true);

    if (type === 'Buyer') {
      const payload = {
        ...buyer,
        authorizationFileId: Number(authId) || 0,
        payInCurrencyId: !values.payInCurrencyId || values.payInCurrencyId === '' ? ZERO_GUID : values.payInCurrencyId,
        typeOfHomeId: !values.typeOfHomeId || values.typeOfHomeId === '' ? ZERO_GUID : values.typeOfHomeId,
        ageOfHomeId: !values.ageOfHomeId || values.ageOfHomeId === '' ? ZERO_GUID : values.ageOfHomeId,
        propertyTypeId: !values.propertyTypeId || values.propertyTypeId === '' ? ZERO_GUID : values.propertyTypeId,
        priceRangeMin: values.priceRangeMin === '' || values.priceRangeMin == null ? null : Number(values.priceRangeMin),
        priceRangeMax: values.priceRangeMax === '' || values.priceRangeMax == null ? null : Number(values.priceRangeMax),
        numBedrooms: values.numBedrooms === '' || values.numBedrooms == null ? null : parseInt(values.numBedrooms, 10),
        numBathrooms: values.numBathrooms === '' || values.numBathrooms == null ? null : parseInt(values.numBathrooms, 10),
        squareFeet: values.squareFeet != null ? String(values.squareFeet) : '',
        familiarWithArea: values.familiarWithArea ?? '',
        fireplace: values.fireplace ?? '',
        garage: values.garage ?? '',
        basement: values.basement ?? '',
        schoolRequestNote: values.schoolRequestNote ?? '',
        preferredAgentName: values.preferredAgentName ?? '',
        preferredAgency: values.preferredAgency ?? '',
        preferredAgencyEmail: values.preferredAgencyEmail ?? '',
        preferredAgencyPhone: values.preferredAgencyPhone ?? '',
       // mortgageLenderSelected: values.mortgageLenderSelected ?? '',
        specialNeeds: values.specialNeeds ?? '',
        hasSchoolRequest: values.hasSchoolRequest ?? false,
        hasPreferredAgent: values.hasPreferredAgent ?? false
      };

      const resp = await props.updateAuthDestinationServicesBuyer(authId, payload);
      if (isFailure(resp)) {
        props.showToast('Failed to update the employee destination services info, please try again.', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });
        setIsLoading(false);
        return false;
      } else {
        await props.getAuthDestinationServices(authId);
        setIsLoading(false);
        return true;
      }
    } else {
      const payload = {
        ...renter,
        authorizationFileId: Number(authId) || 0,
        payInCurrencyId: !values.renterCurrency || values.renterCurrency === '' ? ZERO_GUID : values.renterCurrency,
        priceRangeMin: values.renterPriceRangeMin === '' || values.renterPriceRangeMin == null ? null : Number(values.renterPriceRangeMin),
        priceRangeMax: values.renterPriceRangeMax === '' || values.renterPriceRangeMax == null ? null : Number(values.renterPriceRangeMax),
        numBedrooms: values.renterNumBedrooms === '' || values.renterNumBedrooms == null ? null : parseInt(values.renterNumBedrooms, 10),
        numBathrooms:
          values.renterNumBathrooms === '' || values.renterNumBathrooms == null ? null : parseInt(values.renterNumBathrooms, 10),
        leaseAmount: values.leaseAmount === '' || values.leaseAmount == null ? null : Number(values.leaseAmount),
        penaltyDepositAmount:
          values.pentaltyDepositAmount === '' || values.pentaltyDepositAmount == null ? null : Number(values.pentaltyDepositAmount),
        leaseStartDate: values.leaseStartDate ?? null,
        leaseEndDate: values.leaseEndDate ?? null,
        schoolRequest: values.renterSchoolRequest ?? false,
        schoolRequestNote: values.renterSchoolRequestNote ?? '',
        hasRentalSecured: values.hasRentalSecured ?? false,
        specialNeeds: values.specialNeeds ?? '',
        areaTourId: values.areaTour ? renter?.areaTourId : null,
        areaTourInfo: {
          areaTourId: values.areaTour ? renter?.areaTourId : null,
          areaTourInitiationDate: values.areaTour ? values.areaTourInitiationDate : null,
          areaTourCanceledDate: values.areaTour ? values.areaTourCanceledDate : null,
          areaTourCompletedDate: values.areaTour ? values.areaTourCompletedDate : null,
          areaTourAgentId: values.areaTour ? values.areaTourAgentId : 0
        }
      };

      const resp = await props.updateAuthDestinationServicesRenter(authId, payload);
      if (isFailure(resp)) {
        props.showToast('Failed to update the renter destination services info, please try again.', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });
        setIsLoading(false);
        return false;
      } else {
        await props.getAuthDestinationServices(authId);
        setIsLoading(false);
        return true;
      }
    }
  };

  const handleSaveEdits = async values => {
    const resp = await updateFields(values);
    if (!resp) {
      props.showToast('Failed to update card data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    setEditingModalOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ marginTop: '16px' }}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between align-center">
              <div className={classes.iconRow}>
                <Tooltip title="Estimates" key="Estimates" placement="right">
                  <FontAwesomeIcon icon={['fas', 'fa-truck-moving']} size="2x" />
                </Tooltip>
                <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  {type === 'Renter' ? 'Renter Preferences' : 'Buyer Preferences'}
                </Typography>
              </div>
              {!props.hideEdit && (
                <>
                  <Fab className={classes.iconOverlayImg} color="primary" size="small" onClick={() => setEditingModalOpen(true)}>
                    <FontAwesomeIcon size="1x" icon={['fas', 'pen']} color={theme.palette.common.white} />
                  </Fab>
                </>
              )}
            </div>
          </CardContent>

          <CardContent sx={{ padding: '10px', marginLeft: '30px' }}>
            <Grid container spacing={2} columns={{ xs: 4, sm: 4, md: 4, xl: 4 }}>
              {type === 'Renter' && (
                <>
                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Area Tour
                        </Typography>
                      </div>
                      <div>
                        <FormControl sx={{ marginTop: '-10px', marginBottom: '1px' }}>
                          <FormControlLabel
                            key={'areaTour'}
                            name={'areaTour'}
                            control={<Checkbox />}
                            checked={areaTour}
                            disabled={true}
                            variant="standard"
                            margin="dense"
                          />
                        </FormControl>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Currency
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {displayCurrencyTitle(renter.payInCurrencyId) || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Min Price
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {renter?.priceRangeMin ? `$${renter.priceRangeMin}` : <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div sx={{ marginLeft: '-50px', marginTop: '40px' }}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Max Price
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {renter?.priceRangeMax ? `$${renter.priceRangeMax}` : <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Bedroom Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {renter?.numBedrooms ?? <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Bathroom Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {renter?.numBathrooms ?? <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          School Request
                        </Typography>
                      </div>
                      <div>
                        <FormControl sx={{ marginTop: '-10px', marginBottom: '1px' }}>
                          <FormControlLabel
                            key={'renter.renterSchoolRequest'}
                            name={'renter.renterSchoolRequest'}
                            control={<Checkbox />}
                            checked={renter?.schoolRequest || false}
                            disabled={true}
                            variant="standard"
                            margin="dense"
                          />
                        </FormControl>
                      </div>
                    </Box>
                  </Grid>

                  {renter?.schoolRequest && (
                    <Grid item xs={2} sm={2} md={1} xl={1}>
                      <Box>
                        <div className={classes.mb2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                            School Request Note
                          </Typography>
                        </div>
                        <div className={classes.mb2}>
                          <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                            {renter?.schoolRequestNote || <div>&mdash;</div>}
                          </Typography>
                        </div>
                      </Box>
                    </Grid>
                  )}
                  {areaTour && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ margin: '20px 0' }} />
                      </Grid>
                      <Grid item xs={2} sm={2} md={1} xl={1}>
                        <Box>
                          <div className={classes.mb2}>
                            <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                              Area Tour Agent
                            </Typography>
                          </div>
                          <div className={classes.mb2}>
                            <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                              {areaTour.areaTourAgentId && areaTour.areaTourAgentId > 0
                                ? brokerAgents.find(a => a.agentId === areaTour.areaTourAgentId)?.firstName
                                : ''}
                            </Typography>
                          </div>
                        </Box>
                      </Grid>
                      <Grid item xs={2} sm={2} md={1} xl={1}>
                        <Box>
                          <div className={classes.mb2}>
                            <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                              Area Tour Initiation Date
                            </Typography>
                          </div>
                          <div className={classes.mb2}>
                            <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                              {areaTour?.areaTourInitiationDate ? (
                                new Date(areaTour.areaTourInitiationDate).toLocaleDateString('en-us')
                              ) : (
                                <div>&mdash;</div>
                              )}
                            </Typography>
                          </div>
                        </Box>
                      </Grid>

                      <Grid item xs={2} sm={2} md={1} xl={1}>
                        <Box>
                          <div className={classes.mb2}>
                            <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                              Area Tour Canceled Date
                            </Typography>
                          </div>
                          <div className={classes.mb2}>
                            <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                              {areaTour?.areaTourCanceledDate ? (
                                new Date(areaTour.areaTourCanceledDate).toLocaleDateString('en-us')
                              ) : (
                                <div>&mdash;</div>
                              )}
                            </Typography>
                          </div>
                        </Box>
                      </Grid>

                      <Grid item xs={2} sm={2} md={1} xl={1}>
                        <Box>
                          <div className={classes.mb2}>
                            <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                              Area Tour Completed Date
                            </Typography>
                          </div>
                          <div className={classes.mb2}>
                            <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                              {areaTour?.areaTourCompletedDate ? (
                                new Date(areaTour.areaTourCompletedDate).toLocaleDateString('en-us')
                              ) : (
                                <div>&mdash;</div>
                              )}
                            </Typography>
                          </div>
                        </Box>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ margin: '20px 0' }} />
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Rental Secured
                        </Typography>
                      </div>
                      <div>
                        <FormControl sx={{ marginTop: '-10px', marginBottom: '1px' }}>
                          <FormControlLabel
                            key={'renter.hasRentalSecured'}
                            name={'renter.hasRentalSecured'}
                            control={<Checkbox />}
                            checked={renter?.hasRentalSecured || false}
                            disabled={true}
                            variant="standard"
                            margin="dense"
                          />
                        </FormControl>
                      </div>
                    </Box>
                  </Grid>

                  {renter?.hasRentalSecured && (
                    <>
                      <Grid item xs={2} sm={2} md={1} xl={1}>
                        <Box>
                          <div className={classes.mb2}>
                            <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                              Lease Start Date
                            </Typography>
                          </div>
                          <div className={classes.mb2}>
                            <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                              {renter?.leaseStartDate ? new Date(renter.leaseStartDate).toLocaleDateString('en-us') : <div>&mdash;</div>}
                            </Typography>
                          </div>
                        </Box>
                      </Grid>

                      <Grid item xs={2} sm={2} md={1} xl={1}>
                        <Box>
                          <div className={classes.mb2}>
                            <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                              Deposit
                            </Typography>
                          </div>
                          <div className={classes.mb2}>
                            <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                              {renter?.penaltyDepositAmount ? formatMoney(renter.penaltyDepositAmount) : <div>&mdash;</div>}
                            </Typography>
                          </div>
                        </Box>
                      </Grid>
                    </>
                  )}
                </>
              )}
              {type === 'Buyer' && (
                <>
                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Currency
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {displayCurrencyTitle(buyer.payInCurrencyId) || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Min Price
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.priceRangeMin ? `$${buyer.priceRangeMin}` : <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div sx={{ marginLeft: '-50px', marginTop: '40px' }}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Max Price
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.priceRangeMax ? `$${buyer.priceRangeMax}` : <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Bedroom Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.numBedrooms ?? <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Bathroom Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.numBathrooms ?? <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Type of Home
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {displayTypeOfHome() || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Age of Home
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {displayAgeOfHome() || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Square Feet
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.squareFeet || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Property Type
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {displayPropertyType() || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Familiar with Area
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.familiarWithArea || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Fireplace Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.fireplace || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Garage Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.garage || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Basement Preference
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.basement || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          School Request
                        </Typography>
                      </div>
                      <div>
                        <FormControl sx={{ marginTop: '-10px', marginBottom: '1px' }}>
                          <FormControlLabel
                            key={'buyer.hasSchoolRequest'}
                            name={'buyer.hasSchoolRequest'}
                            control={<Checkbox />}
                            checked={buyer?.hasSchoolRequest || false}
                            disabled={true}
                            variant="standard"
                            margin="dense"
                          />
                        </FormControl>
                      </div>
                    </Box>
                  </Grid>

                  {buyer?.hasSchoolRequest && (
                    <Grid item xs={2} sm={2} md={1} xl={1}>
                      <Box>
                        <div className={classes.mb2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                            School Request Note
                          </Typography>
                        </div>
                        <div className={classes.mb2}>
                          <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                            {buyer?.schoolRequestNote || <div>&mdash;</div>}
                          </Typography>
                        </div>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Special Needs and Important Features
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.specialNeeds || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ margin: '20px 0' }} />
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Preferred Agent Name
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.preferredAgentName || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Preferred Agency
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.preferredAgency || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Agency Email
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.preferredAgencyEmail || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} xl={1}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Agency Phone
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.preferredAgencyPhone || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ margin: '20px 0' }} />
                  </Grid>

                  {/* <Grid item xs={2} sm={2} md={2} xl={2}>
                    <Box>
                      <div className={classes.mb2}>
                        <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                          Mortgage Lender Selected
                        </Typography>
                      </div>
                      <div className={classes.mb2}>
                        <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                          {buyer?.mortgageLenderSelected || <div>&mdash;</div>}
                        </Typography>
                      </div>
                    </Box>
                  </Grid> */}
                </>
              )}
            </Grid>
            {type === 'Renter' && (
              <Button
                color="secondary"
                type="submit"
                variant="contained"
                sx={{ maxHeight: '40px', marginTop: '20px' }}
                disabled={!renter?.priceRangeMin || !renter?.priceRangeMax || !renter?.numBedrooms || !renter?.numBathrooms}
              >
                Send Rental Referral Letter
              </Button>
            )}

            {type === 'Buyer' && (
              <Button color="secondary" type="button" variant="contained" sx={{ maxHeight: '40px', marginTop: '20px' }}>
                Send Agent List
              </Button>
            )}
          </CardContent>
        </Box>
      </Card>

      {editingModalOpen && (
        <EditCardModal
          open={editingModalOpen}
          isLoading={isLoading}
          onClose={() => setEditingModalOpen(false)}
          onSubmit={handleSaveEdits}
          buyer={buyer}
          enumerations={enumerations}
          renter={renter}
          type={type}
          brokerAgents={brokerAgents}
          classes={classes}
        />
      )}
    </ThemeProvider>
  );
};

export default compose(
  withStyles(styles),
  connect(null, {
    updateAuthDestinationServicesBuyer,
    updateAuthDestinationServicesRenter,
    getAuthDestinationServices,
    showToast
  })
)(DestinationServicesMainEditableCard);
